import { PrinterOutlined } from '@ant-design/icons';
import { Button, Form, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { excelDateRangeFormat } from '../../../../common/utils/excelDateRangeFormat';
import DownloadExcelReport from '../../../../components/common/ExcelReport';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import ReportsHeader from '../../../../components/common/ReportsHeader';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import FormHeaderTitle from '../../../Invoice(Visa)/Components/FormHeaderTitle';
import { useLazyGetTourPackageReportListQuery } from '../endpoints/TourPackageReportEndpoints';
import { ITourPackageReportList, ITourPackageReportPost } from '../types/TourPackageTypes';
import TourPackageReportColumn from '../utils/TourPackageReportColumn';
import { Fixed2 } from '../../../../common/utils/common.utils';

const TourPackageReport = () => {
  const [form] = Form.useForm();
  const [date_range_value, setDate_range_value] = useState<string>();

  const [fetchData, { data, isLoading, isFetching }] = useLazyGetTourPackageReportListQuery();

  const totalAmount = data?.data?.reduce((acc: number, curr: ITourPackageReportList) => {
    return acc + Number(curr.invoice_net_total);
  }, 0);

  const onFinish = async (values: ITourPackageReportPost) => {
    const setFrom_date = dayjs(values.date_range[0]).format('YYYY-MM-DD');
    const setTo_date = dayjs(values.date_range[1]).format('YYYY-MM-DD');
    const date_range = `from_date=${setFrom_date}&to_date=${setTo_date}`;
    setDate_range_value(date_range);
    const body = {
      from_date: setFrom_date,
      to_date: setTo_date,
      client: values.invoice_combclient_id,
      group_id: values.group_id,
    };
    await fetchData(body);
  };

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Tour_Package_Report`,
  });

  // @HANDLE CONTENTS
  const jsx_com = (
    <Table
      columns={TourPackageReportColumn()}
      bordered
      size='small'
      dataSource={data?.data}
      loading={{ spinning: isLoading || isFetching, indicator: loadingIndicator }}
      pagination={false}
      summary={(_) => {
        return (
          <>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={8} align='right'>
                <Typography
                  style={{
                    textAlign: 'right',
                    marginRight: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  Total:
                </Typography>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align='right'>
                <Typography
                  style={{
                    textAlign: 'right',
                    marginRight: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {Fixed2(totalAmount)}
                </Typography>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        );
      }}
    />
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'Tour Package Report' }}
        extraInfo={{
          date:
            date_range_value
              ?.split('&')
              ?.map((param) => dayjs(param.split('=')[1])?.format('DD-MM-YYYY'))
              ?.join(' to ') || '',
        }}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'Tour Package Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>
        <DownloadExcelReport
          excelName={`tour_package_report_${excelDateRangeFormat(date_range_value as string)}`}
          description={`TOUR PACKAGE REPORT`}
          excelTableHead={[
            'Date',
            'Invoice No.',
            'Client Name',
            'Journey Date',
            'Group Name',
            'Total Days',
            'Total Nights',
            'Total Amount',
          ]}
          excelData={
            data?.data?.length
              ? data.data.map((item: any) => ({
                  Date: item?.invoice_sales_date
                    ? dayjs(item.invoice_sales_date).format('DD MMM YYYY')
                    : '',
                  'Invoice No.': item?.invoice_no || 'N/A',
                  'Client Name': item?.client_name || 'N/A',
                  'Journey Date': `${dayjs(item.itour_from_date).format('DD MMM YYYY')} - ${dayjs(
                    item.itour_to_date
                  ).format('DD MMM YYYY')}`,
                  'Group Name': item?.group_name || 'N/A',
                  'Total Days': item?.itour_day || 'N/A',
                  'Total Nights': item?.itour_night || 'N/A',
                  'Total Amount': item?.invoice_net_total || 'N/A',
                }))
              : []
          }
        />
      </Space>
      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{
          invoice_combclient_id: 'all',
          group_id: 'all',
          date_range: [dayjs(), dayjs()],
        }}
      >
        <ReportsHeader date_range required showSelectClients selectTourGroup />
      </Form>

      <>
        <FormHeaderTitle title='Tour Package Report' />
        {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
        {jsx_com}
      </>

      {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
      {print_content}
    </>
  );
};

export default TourPackageReport;
